import { AssetAffected } from "../../data/Fleet/AssetAffected";
import { getIncidentTypesForAsset } from "../../data/Fleet/IncidentType";
import { location } from "../../data/Fleet/Location";

const incidentLocationOptions = location.map((option) => ({
  ...option,
  label:
    option.value && option.value !== "Unknown"
      ? `${option.label} (${option.value})`
      : option.label,
}));

export const incidentFieldConfiguration = [
  {
    name: "complaint",
    label: "Complaint",
    type: "text",
    validation: {
      required: "Complaint description is required",
      minLength: {
        value: 5,
        message: "Complaint must be at least 5 characters long",
      },
      maxLength: {
        value: 100,
        message: "Complaint must not exceed 100 characters",
      },
    },
  },
  {
    name: "assetAffected",
    label: "Asset Affected",
    type: "select",
    options: AssetAffected,
    validation: { required: "Asset Affected is required" },
  },
  {
    name: "assetNumber",
    label: "Asset Number",
    type: "combo",
    dataKey: "assets",
    validation: {
      required: "Asset Number is required",
    },
  },
  {
    name: "driverId",
    label: "Driver",
    type: "combo",
    dataKey: "drivers",
    validation: {
      required: "Driver is required",
    },
  },
  {
    name: "receivedBy",
    label: "Received By",
    type: "text",
    validation: {
      required: "Received by person is required",
    },
  },
  {
    name: "coordinates",
    label: "Coordinates",
    type: "text",
    placeholder: "Enter latitude and longitude (e.g., 40.7128, -74.0060)",
    validation: {
      pattern: {
        value: /^-?\d{1,2}\.\d{3,},\s?-?\d{1,3}\.\d{3,}$/,
        message:
          "Enter a valid latitude and longitude (e.g., 40.7128, -74.0060)",
      },
      custom: {
        validator: (value: string) => {
          const parts = value.split(",").map((part) => parseFloat(part.trim()));
          const latitude = parts[0];
          const longitude = parts[1];
          if (latitude < -90 || latitude > 90) {
            return "Latitude must be between -90 and 90";
          }
          if (longitude < -180 || longitude > 180) {
            return "Longitude must be between -180 and 180";
          }
          return true;
        },
        message:
          "Latitude must be between -90 and 90, and longitude between -180 and 180",
      },
    },
  },
];

export const incidentInitialValues = {
  complaint: "",
  assetAffected: "",
  assetNumber: "",
  driverId: "",
  coordinates: null,
  receivedBy: "",
};

export const makeFieldConfigDynamic = (
  fields: any[],
  setFieldState: (type: string) => void,
  assetAffected: string,
) => {
  return fields.map((field) => {
    if (field.name === "assetAffected") {
      return {
        ...field,
        onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
          setFieldState(event.target.value),
      };
    } else if (field.name === "assetNumber") {
      return {
        ...field,
        disabled: assetAffected === "",
      };
    } else if (field.name === "incidentType") {
      return {
        ...field,
        options: getIncidentTypesForAsset(assetAffected),
        disabled: assetAffected === "",
      };
    }
    return field;
  });
};

export const updateIncidentFieldConfiguration = [
  {
    name: "assetAffected",
    label: "Asset Affected",
    type: "select",
    options: AssetAffected,
    validation: { required: "Asset Affected is required" },
  },
  {
    name: "assetNumber",
    label: "Asset Number",
    type: "combo",
    dataKey: "assets",
    validation: { required: "Asset Number is required" },
  },
  {
    name: "driverId",
    label: "Driver",
    type: "combo",
    dataKey: "drivers",
    validation: { required: "Driver is required" },
  },
  {
    name: "openDate",
    label: "Open Date",
    type: "date",
    validation: { required: "Open date is required" },
  },
  {
    name: "complaint",
    label: "Complaint",
    type: "text",
    validation: {
      required: "Complaint description is required",
      minLength: {
        value: 5,
        message: "Complaint must be at least 5 characters long",
      },
      maxLength: {
        value: 100,
        message: "Complaint must not exceed 100 characters",
      },
    },
  },
  {
    name: "receivedBy",
    label: "Received By",
    type: "text",
    validation: {
      required: "Received by person is required",
    },
  },
  {
    name: "location",
    label: "Location",
    type: "select",
    options: incidentLocationOptions,
    validation: {
      required: "Location is required",
      validate: (value: string) =>
        value !== "Unknown" || "Please select a known location",
    },
  },
  {
    name: "incidentType",
    label: "Incident Type",
    type: "select",
    options: getIncidentTypesForAsset(),
    validation: {
      required: "Incident Type is required",
      validate: (value: string) =>
        value !== "New" || "Please select an incident type",
    },
  },
  {
    name: "coordinates",
    label: "Coordinates",
    type: "text",
    placeholder: "Enter latitude and longitude (e.g., 40.7128, -74.0060)",
    validation: {
      pattern: {
        value: /^-?\d{1,2}\.\d{3,},\s?-?\d{1,3}\.\d{3,}$/,
        message:
          "Enter a valid latitude and longitude (e.g., 40.7128, -74.0060)",
      },
      custom: {
        validator: (value: string) => {
          const parts = value.split(",").map((part) => parseFloat(part.trim()));
          const latitude = parts[0];
          const longitude = parts[1];
          if (latitude < -90 || latitude > 90) {
            return "Latitude must be between -90 and 90";
          }
          if (longitude < -180 || longitude > 180) {
            return "Longitude must be between -180 and 180";
          }
          return true;
        },
        message:
          "Latitude must be between -90 and 90, and longitude between -180 and 180",
      },
    },
  },
];

export const updateIncidentFromRepairTableFieldConfiguration = [
  {
    name: "openDate",
    label: "Open Date",
    type: "date",
    validation: {
      required: "Open date is required",
    },
  },
  {
    name: "complaint",
    label: "Complaint",
    type: "text",
    validation: {
      required: "Complaint description is required",
      minLength: {
        value: 5,
        message: "Complaint must be at least 5 characters long",
      },
      maxLength: {
        value: 100,
        message: "Complaint must not exceed 100 characters",
      },
    },
  },
  {
    name: "receivedBy",
    label: "Received By",
    type: "text",
    validation: {
      required: "Received By is required",
    },
  },
  {
    name: "location",
    label: "Location",
    type: "select",
    options: incidentLocationOptions,
    validation: {
      required: "Location is required",
      validate: (value: string) =>
        value !== "Unknown" || "Please select a known location",
    },
  },
  {
    name: "incidentType",
    label: "Incident Type",
    type: "select",
    options: getIncidentTypesForAsset(),
    validation: {
      required: "Incident Type is required",
      validate: (value: string) =>
        value !== "New" || "Please select an incident type",
    },
  },
  {
    name: "coordinates",
    label: "Coordinates",
    type: "text",
    placeholder: "Enter latitude and longitude (e.g., 40.7128, -74.0060)",
    validation: {
      pattern: {
        value: /^-?\d{1,2}\.\d{3,},\s?-?\d{1,3}\.\d{3,}$/,
        message:
          "Enter a valid latitude and longitude (e.g., 40.7128, -74.0060)",
      },
      custom: {
        validator: (value: string) => {
          const parts = value.split(",").map((part) => parseFloat(part.trim()));
          const latitude = parts[0];
          const longitude = parts[1];
          if (latitude < -90 || latitude > 90) {
            return "Latitude must be between -90 and 90";
          }
          if (longitude < -180 || longitude > 180) {
            return "Longitude must be between -180 and 180";
          }
          return true;
        },
        message:
          "Latitude must be between -90 and 90, and longitude between -180 and 180",
      },
    },
  },
];

export const getUpdateIncidentFieldConfiguration = (
  assetAffected?: string | null,
) =>
  updateIncidentFieldConfiguration.map((field) =>
    field.name === "incidentType"
      ? { ...field, options: getIncidentTypesForAsset(assetAffected) }
      : field,
  );

export const getUpdateIncidentFromRepairTableFieldConfiguration = (
  assetAffected?: string | null,
) =>
  updateIncidentFromRepairTableFieldConfiguration.map((field) =>
    field.name === "incidentType"
      ? { ...field, options: getIncidentTypesForAsset(assetAffected) }
      : field,
  );
